import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { FilterValue, FilterValues } from '@nucleus-commerce/admin-kit/index-table';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useProductHealth } from '../hooks/use-product-health';

const LOW_OR_OUT: FilterValue = { op: 'in', values: ['low_stock', 'out_of_stock'] };

const TILE = 'bg-background flex flex-col gap-1.5 rounded-lg border px-3.5 py-3 text-left';
const NUMBER = 'text-2xl font-semibold tabular-nums';
const ACTION = 'text-primary text-xs font-medium';

export interface ProductHealthTilesProps {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
  /** Where the sell-through tile's report opens; reports live under a store. */
  reportStoreId: string | undefined;
}

/**
 * One-click filters over the master list. A tile's count is the set its filter lists; it adds to
 * the filters on screen, so it shows as a chip, and a second click takes it off.
 */
export function ProductHealthTiles({
  filters,
  onFiltersChange,
  reportStoreId,
}: ProductHealthTilesProps) {
  const { t } = useTranslation('products');
  const format = useFormat();
  const { data } = useProductHealth(true);

  const tile = (label: string, count: number | undefined, key: string, value: FilterValue) => {
    const on = JSON.stringify(filters[key]) === JSON.stringify(value);
    return (
      <button
        key={key}
        type="button"
        aria-pressed={on}
        onClick={() => {
          const { [key]: _removed, ...rest } = filters;
          onFiltersChange(on ? rest : { ...rest, [key]: value });
        }}
        className={cn(
          TILE,
          'hover:bg-muted/50 focus-visible:ring-ring outline-none focus-visible:ring-2',
          on && 'border-primary',
        )}
      >
        <span className="text-muted-foreground text-sm">{label}</span>
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn(NUMBER, count !== undefined && count > 0 && 'text-destructive')}>
            {count ?? '–'}
          </span>
          <span className={ACTION}>{on ? t('health.showing') : t('health.show')}</span>
        </span>
      </button>
    );
  };

  return (
    <section aria-label={t('health.label')} className="grid grid-cols-2 gap-3 pb-4 lg:grid-cols-4">
      {tile(t('health.noImage'), data?.noImage, 'hasImage', { op: 'eq', value: false })}
      {tile(t('health.notInAnyStore'), data?.notInAnyStore, 'inAnyStore', {
        op: 'eq',
        value: false,
      })}
      {tile(t('health.lowOrOutOfStock'), data?.lowOrOutOfStock, 'stock', LOW_OR_OUT)}
      {data?.sellThrough !== null && data?.sellThrough !== undefined ? (
        <div className={TILE}>
          <span className="text-muted-foreground text-sm">{t('health.sellThrough')}</span>
          <span className="flex items-baseline justify-between gap-2">
            <span className={NUMBER}>{format.percent(data.sellThrough)}</span>
            {reportStoreId ? (
              <Link
                to="/s/$storeId/reports/products"
                params={{ storeId: reportStoreId }}
                className={cn(ACTION, 'hover:underline')}
              >
                {t('health.report')}
              </Link>
            ) : null}
          </span>
        </div>
      ) : null}
    </section>
  );
}
